const NATIVE_NEWEST_SORT = "native-newest";

function loadScriptOnce(src, globalName) {
  if (window[globalName]) return Promise.resolve(window[globalName]);

  const existing = document.querySelector(`script[data-partbot-src="${src}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window[globalName]), { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.partbotSrc = src;
    script.onload = () => resolve(window[globalName]);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function normalizeSearchHost(host, fallback) {
  const value = host || fallback;
  return value.startsWith("http") ? value : `https://${value}`;
}

function sortedRequestOnPrimaryIndex(request, primaryIndexName, { dropSort = false } = {}) {
  if (!request || !primaryIndexName) return request;

  const requestIndexName = request.indexName || request.indexUid || "";
  if (requestIndexName === primaryIndexName && dropSort && request.params?.sort) {
    const { sort, ...params } = request.params;
    return {
      ...request,
      indexName: primaryIndexName,
      indexUid: primaryIndexName,
      params,
    };
  }

  const sortPrefix = `${primaryIndexName}:`;
  if (!requestIndexName.startsWith(sortPrefix)) return request;

  const sortValue = requestIndexName.slice(sortPrefix.length);
  return {
    ...request,
    indexName: primaryIndexName,
    indexUid: primaryIndexName,
    params: {
      ...(request.params || {}),
      ...(dropSort || !sortValue ? {} : { sort: [sortValue] }),
    },
  };
}

function sortedRequestsOnPrimaryIndex(requests, primaryIndexName, options = {}) {
  if (!Array.isArray(requests)) return requests;
  return requests.map((request) => sortedRequestOnPrimaryIndex(request, primaryIndexName, options));
}

function isUnsupportedSortError(error) {
  const message = String(error?.message || error || "");
  return message.includes("is not sortable") || message.includes("invalid_search_sort");
}

async function fetchSearchConfig(apiHost, apiKey, store) {
  if (store) {
    try {
      const response = await fetch(
        `${apiHost}/v1/storefront-search-configs/${encodeURIComponent(store)}.js?v=${Math.floor(Date.now() / 60_000)}`,
        { credentials: "omit" },
      );
      if (!response.ok) throw new Error(`Search config failed: ${response.status}`);

      const config = await response.json();
      if (config.host && config.store_search_key && config.store_index_name) {
        return config;
      }
    } catch (error) {
      console.warn("[PartbotCollectionSearch] Public search config unavailable; using auth", error);
    }
  }

  const response = await fetch(`${apiHost}/v1/vehicles/auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      use_store_vehicles: true,
      ...(store ? { store } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error(`Vehicle auth failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

function normalizeFacetAttribute(facet) {
  if (!facet) return facet;
  const rawFacet = String(facet).trim();
  const loweredFacet = rawFacet.toLowerCase();
  const normalizedFacet = loweredFacet.replace(/[\s-]+/g, "_");

  if (
    rawFacet.includes("Brand") ||
    rawFacet.includes("Vendor") ||
    loweredFacet === "brand" ||
    loweredFacet === "vendor"
  ) {
    return "brand";
  }
  if (rawFacet.includes("Product Type") || normalizedFacet === "product_type") {
    return "product_type";
  }
  if (rawFacet.includes("Kit") || rawFacet.includes("Assembly") || normalizedFacet === "assembly") {
    return "assembly";
  }

  const toTitleCase = (value) =>
    String(value || "")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  if (rawFacet.includes(".")) {
    const [prefix, ...rest] = rawFacet.split(".");
    if (!rest.length) return rawFacet;
    return `${prefix}.${toTitleCase(rest.join("."))}`;
  }

  return `attributes.${toTitleCase(rawFacet)}`;
}

function uniq(values) {
  return [...new Set(values.filter(Boolean))];
}

function filterValue(value, { numeric = false } = {}) {
  const stringValue = String(value).trim();
  if (numeric && /^-?\d+(\.\d+)?$/.test(stringValue)) return stringValue;
  return JSON.stringify(stringValue);
}

function quoteFilterAttribute(attribute) {
  const unquotedAttribute = String(attribute || "")
    .trim()
    .replace(/^["']|["']$/g, "");

  if (!unquotedAttribute) return unquotedAttribute;
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(unquotedAttribute)) return unquotedAttribute;
  return JSON.stringify(unquotedAttribute);
}

function equalsFilter(attribute, value, options = {}) {
  return `${quoteFilterAttribute(attribute)} = ${filterValue(value, options)}`;
}

function notEqualsFilter(attribute, value, options = {}) {
  return `${quoteFilterAttribute(attribute)} != ${filterValue(value, options)}`;
}

function numericComparisonFilter(attribute, operator, value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  return `${quoteFilterAttribute(attribute)} ${operator} ${numericValue}`;
}

function normalizeLegacyFilterValue(value) {
  const trimmedValue = String(value || "").trim();

  if (/^(true|false)$/i.test(trimmedValue)) return trimmedValue.toLowerCase();
  if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) return trimmedValue;

  if (
    (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) ||
    (trimmedValue.startsWith("'") && trimmedValue.endsWith("'"))
  ) {
    return filterValue(trimmedValue.slice(1, -1).replace(/\\(["'])/g, "$1"));
  }

  return filterValue(trimmedValue);
}

function normalizeLegacySearchFilters(filters) {
  if (!filters) return "";

  return filters.replace(
    /(^|[\s(])((?:"[^"]+"|'[^']+'|[A-Za-z_][A-Za-z0-9_.[\]-]*))\s*:\s*("(?:\\"|[^"])*"|'(?:\\'|[^'])*'|true|false|-?\d+(?:\.\d+)?|[^\s)]+)/gi,
    (match, prefix, attribute, value) =>
      `${prefix}${quoteFilterAttribute(attribute)} = ${normalizeLegacyFilterValue(value)}`,
  );
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function maskKey(key) {
  if (!key) return key;
  if (key.length <= 12) return key;
  return `${key.slice(0, 6)}...${key.slice(-6)}`;
}

function pickFacetDataByAttribute(source, attribute) {
  if (!source || !attribute) return null;
  if (source[attribute]) return source[attribute];

  const target = String(attribute).toLowerCase();
  const matchedKey = Object.keys(source).find((key) => String(key).toLowerCase() === target);
  return matchedKey ? source[matchedKey] : null;
}

function resolveHitHandle(hit) {
  if (!hit || typeof hit !== "object") return "";
  if (hit.handle) return String(hit.handle).trim();
  if (hit.product_handle) return String(hit.product_handle).trim();
  if (hit.slug) return String(hit.slug).trim();
  if (hit.url) {
    const match = String(hit.url).match(/\/products\/([^/?#]+)/);
    if (match && match[1]) return decodeURIComponent(match[1]).trim();
  }
  return "";
}

function resolveInitialSortIndex(searchIndexName, preferredSort) {
  const sort = String(preferredSort || "").trim().toLowerCase();
  if (!sort) return searchIndexName;

  if (sort === "best-selling" || sort === "best_selling") {
    return `${searchIndexName}:quantity_sold:desc`;
  }
  if (sort === "price-ascending" || sort === "price_ascending") {
    return `${searchIndexName}:price_from:asc`;
  }
  if (sort === "price-descending" || sort === "price_descending") {
    return `${searchIndexName}:price_from:desc`;
  }
  if (sort === "title-ascending" || sort === "title_ascending") {
    return `${searchIndexName}:title:asc`;
  }
  if (sort === "title-descending" || sort === "title_descending") {
    return `${searchIndexName}:title:desc`;
  }

  return searchIndexName;
}

function isManualSortValue(value) {
  const sort = String(value || "").trim().toLowerCase();
  return sort === "manual" || sort === "relevance";
}

function resolveSortParam(preferredSort) {
  const sort = String(preferredSort || "").trim().toLowerCase();
  if (!sort || isManualSortValue(sort)) return "";

  if (sort.includes(":")) {
    const parts = sort.split(":");
    return parts.slice(-2).join(":");
  }

  if (sort === "created-descending" || sort === "created_descending") return NATIVE_NEWEST_SORT;
  if (sort === "best-selling" || sort === "best_selling") return "quantity_sold:desc";
  if (sort === "price-ascending" || sort === "price_ascending") return "price_from:asc";
  if (sort === "price-descending" || sort === "price_descending") return "price_from:desc";
  if (sort === "title-ascending" || sort === "title_ascending") return "title:asc";
  if (sort === "title-descending" || sort === "title_descending") return "title:desc";

  return "";
}

function parseManualProductIds(value) {
  return String(value || "").match(/\d+/g) || [];
}

function parseCollectionRules(value) {
  return String(value || "")
    .split("||")
    .map((rule) => {
      const [column, relation, ...conditionParts] = rule.split("::");
      const condition = conditionParts.join("::").trim();
      return {
        column: String(column || "").trim().toLowerCase().replace(/[\s-]+/g, "_"),
        relation: String(relation || "").trim().toLowerCase().replace(/[\s-]+/g, "_"),
        condition,
      };
    })
    .filter((rule) => rule.column && rule.condition);
}

// Ask Shopify for membership instead of reconstructing collection rules.
// Forward native filters and tagged paths so membership matches the rendered URL.
function collectionMembershipUrl(collectionHandle, page, sortBy = null) {
  const root = window.Shopify?.routes?.root || "/";
  const basePath = `${root.endsWith("/") ? root : `${root}/`}collections/${encodeURIComponent(collectionHandle)}`;
  const current = new URL(window.location.href);
  const path = current.pathname === basePath || current.pathname.startsWith(`${basePath}/`)
    ? current.pathname : basePath;
  const url = new URL(path, window.location.origin);
  current.searchParams.forEach((value, name) => {
    if (name.startsWith("filter.") || name === "sort_by" || name === "preview_theme_id") url.searchParams.append(name, value);
  });
  if (sortBy) url.searchParams.set("sort_by", sortBy);
  url.searchParams.set("view", "partbot-product-ids");
  url.searchParams.set("page", String(page));
  return url;
}

function needsCollectionMembership(collectionHandle) {
  if (!collectionHandle) throw new Error("Missing collection handle");
  if (collectionHandle !== "all") return true;
  const url = collectionMembershipUrl(collectionHandle, 1);
  return Array.from(url.searchParams.keys()).some((name) => name.startsWith("filter.")) ||
    url.pathname.endsWith("/all") === false;
}

async function fetchCollectionProductIdPage(collectionHandle, page, sortBy = null) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(collectionMembershipUrl(collectionHandle, page, sortBy).toString(), {
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Collection membership request failed: ${response.status}`);
    const payload = await response.json();
    const validIds = Array.isArray(payload.product_ids) && payload.product_ids.every(
      (id) => /^\d+$/.test(String(id)) && Number(id) > 0,
    );
    if (!validIds || payload.page !== page || !Number.isInteger(payload.pages) ||
        payload.pages < 1 || payload.pages > 500 || !Number.isInteger(payload.items) || payload.items < 0) {
      throw new Error("Invalid collection membership response");
    }
    return { page: payload.page, pages: payload.pages, items: payload.items,
      productIds: payload.product_ids.map(String) };
  } finally {
    clearTimeout(timeout);
  }
}

async function fetchCollectionProductIds(collectionHandle, expectedProductCount, sortBy = null) {
  if (!needsCollectionMembership(collectionHandle)) return [];
  const first = await fetchCollectionProductIdPage(collectionHandle, 1, sortBy);
  const pages = [first];
  for (let page = 2; page <= first.pages; page += 4) {
    const numbers = Array.from({ length: Math.min(4, first.pages - page + 1) }, (_, index) => page + index);
    pages.push(...await Promise.all(numbers.map((number) => fetchCollectionProductIdPage(collectionHandle, number, sortBy))));
  }
  const ids = new Set();
  pages.forEach((page) => {
    if (page.pages !== first.pages || page.items !== first.items) {
      throw new Error("Collection membership changed during pagination");
    }
    page.productIds.forEach((id) => ids.add(id));
  });
  if (first.items !== expectedProductCount || ids.size !== first.items) {
    throw new Error("Incomplete collection membership; use native collection products");
  }
  return Array.from(ids);
}

class PartbotCollectionSearch extends HTMLElement {
  constructor() {
    super();

    this.totalProducts = Number(this.getAttribute("data-total-products"));
    this.historyLocationKey = this.currentHistoryLocationKey();
    this.vehicleHistoryKey = this.currentVehicleHistoryKey();
    this.onHistoryNavigation = this.handleHistoryNavigation.bind(this);
    window.addEventListener("popstate", this.onHistoryNavigation, true);
    if (this.totalProducts === 0) return;

    this.collection = this.getAttribute("data-collection");
    this.collectionType = this.getAttribute("data-collection-type");
    this.defaultSortBy = this.getAttribute("data-sortby");
    this.urlSortBy = new URLSearchParams(window.location.search).get("sort_by") || "";
    this.currentSortValue = resolveSortParam(
      this.urlSortBy || this.defaultSortBy,
    );
    if (this.currentSortValue === NATIVE_NEWEST_SORT && !this.nativeNewestAvailable()) this.currentSortValue = "";
    this.disjunctive = this.getAttribute("data-disjunctive") === "true" ? "OR" : "AND";
    this.collectionRules = parseCollectionRules(this.getAttribute("data-collection-rules"));
    this.productIds = this.getAttribute("data-product-ids");
    this.manualProductIds = parseManualProductIds(this.productIds);
    this.manualProductRank = new Map(
      this.manualProductIds.map((id, index) => [String(id), index]),
    );

    this.skus = [];
    this.vehicle_ids = [];
    this.activeVehicleIds = [];
    this.selectedYear = null;
    this.brands = this.getAttribute("data-vendors")
      ? this.getAttribute("data-vendors").split("|")
      : [];
    this.product_types = this.getAttribute("data-product-types")
      ? this.getAttribute("data-product-types")
          .split("|")
          .filter((str) => str.trim() !== "")
      : [];

    this.facets = this.getAttribute("data-facets")
      ? this.getAttribute("data-facets")
          .split(",")
          .filter((str) => str.trim() !== "")
      : [];

    this.filters = this.getAttribute("data-filters");

    this.tags = this.getAttribute("data-tags")
      ? this.getAttribute("data-tags")
          .split("|")
          .filter((str) => str.trim() !== "")
      : [];
    this.titleContains = this.getAttribute("data-title-contains")
      ? this.getAttribute("data-title-contains")
          .split("|")
          .map((str) => str.trim())
          .filter((str) => str !== "")
      : [];

    this.showVehicleSearch = this.getAttribute("data-show-vehicle-search") === "true";

    this.partbot_vehicle = localStorage.getItem("partbot_selected_vehicle");
    this.partbot_plate_details = localStorage.getItem("partbot_plate_details");
    this.platesearch_ids = [];
    this.vehicleParamFields = [
      "make",
      "model",
      "year",
      "body",
      "series",
      "variant",
      "engine",
      "engine_capacity",
    ];

    const hasPlateSearchVehicle =
      this.showVehicleSearch && this.partbot_vehicle && this.partbot_plate_details;

    if (hasPlateSearchVehicle) {
      this.partbot_vehicle = JSON.parse(this.partbot_vehicle);
      this.platesearch_ids = this.partbot_vehicle.vehicle_ids;

      const vehicleDescription = document.querySelector(".js-vehicle-description");

      if (vehicleDescription) vehicleDescription.textContent = this.partbot_vehicle.description;

      const clearButton = document.querySelector(".js-button-clear-vehicle");
      if (clearButton) {
        clearButton.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();

          localStorage.removeItem("partbot_selected_vehicle");
          localStorage.removeItem("partbot_plate_details");
          location.reload();
        });
      }

      const vehicleNotice = document.querySelector(".js-selected-vehicle-notice");

      if (vehicleNotice) {
        vehicleNotice.classList.remove("hidden");
        vehicleNotice.classList.add("flex");
      }

      const vehicleSearch = document.querySelector("vehicle-search");

      if (vehicleSearch) {
        vehicleSearch.classList.add("hidden");

        const vehicleSearchSection = vehicleSearch.closest("section");

        if (vehicleSearchSection) {
          vehicleSearchSection.setAttribute("data-vehicle-selected", "true");
        }
      }
    }

    this.facetFiltersContainer = this.querySelector("#facet-filters");

    const vehicleSearchRoot = document.querySelector("vehicle-search");
    this.apiKey =
      this.getAttribute("data-api-key") || vehicleSearchRoot?.getAttribute("data-api-key");
    this.store =
      this.getAttribute("data-store") ||
      vehicleSearchRoot?.getAttribute("data-store") ||
      window.location.hostname;
    this.apiHost = "https://api.cartbot.io";
    this.searchHost = "https://search.partbot.io";
    this.searchIndexName = null;
    this.debugPrefix = "[PartbotCollectionSearch][SWD]";
    this.unsupportedFacetAttributes = new Set();
    this.baseFilters = "";
    this.collectionProductIdsFilter = "";
    this.searchFailed = false;
    this.manualProductIdsFilter = "";
    this.manualProductIdFilterEnabled = true;
    this.configureWidget = null;
    this.configureWidgetParams = {};
    this.selectedFacetValues = new Map();
    this.restoreHistoryFilters();
    this.facetLabelsByAttribute = new Map();
    this.facetContainersByAttribute = new Map();
    this.facetOptionState = new Map();
    this.facetSyncTimer = null;
    this.facetSyncToken = 0;
    this.hasReceivedSearchResults = false;
    this.lastResultsCount = null;

    this.hitsContainer = this.querySelector("#partbot-product-grid");

    // hide the hitsContainer if there is a make parameter in the query string
    if (window.location.search.includes("make=")) {
      this.hitsContainer.style.display = "none";
    }

    // URL-derived vehicle search changes are emitted on window only.
    this.lastVehicleSearchEvent = null;
    this.onVehicleSearchChange = this.applyVehicleSearchSelection.bind(this);
    window.addEventListener("vehicle-search-change", this.onVehicleSearchChange);
    document.addEventListener("vehicle-search-change", this.onVehicleSearchChange);
    this.filterAlgoliaHits = this.processHits.bind(this);
    this.onGloboRenderCompleted = this.handleGloboRenderCompleted.bind(this);
    window.addEventListener("globoFilterRenderCompleted", this.onGloboRenderCompleted);

    this.restoreInitialVehicleSelection();
    this.rememberHistoryFilters();
    if (new URLSearchParams(window.location.search).has("make") ||
        (this.showVehicleSearch && (this.activeVehicleIds.length > 0 || this.selectedYear))) {
      const fallback = this.querySelector("#partbot-product-fallback");
      if (fallback) fallback.hidden = true;
    }
    this.hideGloboInjectedUi();

    this.initializeSearch().catch((error) => {
      console.error("[PartbotCollectionSearch] Failed to initialize search", error);
      this.showSearchFallback();
    });
  }

  showSearchFallback() {
    this.searchFailed = true;
    const fallback = this.querySelector("#partbot-product-fallback");
    if (fallback) fallback.hidden = false;
    if (this.hitsContainer) this.hitsContainer.style.display = "none";
    const pagination = this.querySelector("#partbot-pagination");
    if (pagination) pagination.hidden = true;
    let status = this.querySelector("#partbot-search-status");
    if (!status && fallback) {
      status = document.createElement("p");
      status.id = "partbot-search-status";
      status.setAttribute("role", "status");
      fallback.insertAdjacentElement("beforebegin", status);
    }
    const vehicleInUrl = this.vehicleParamFields?.some((field) =>
      new URLSearchParams(window.location.search).has(field),
    );
    if (status) status.textContent = this.activeVehicleIds?.length || this.selectedYear || vehicleInUrl
      ? "Vehicle filtering is temporarily unavailable. Showing collection products."
      : "Search is temporarily unavailable. Showing collection products.";
    this.querySelectorAll(".partbot-filter-container, #partbot-clear-filters, [data-partbot-toolbar]")
      .forEach((control) => { control.hidden = true; });
  }

  getStoredVehicleSelection() {
    try {
      return JSON.parse(localStorage.getItem("partbot_selected_vehicle") || "null");
    } catch (error) {
      return null;
    }
  }

  disconnectedCallback() {
    window.removeEventListener("popstate", this.onHistoryNavigation, true);
  }

  currentHistoryLocationKey() {
    return window.location.pathname + window.location.search;
  }

  currentVehicleHistoryKey() {
    const params = new URLSearchParams(window.location.search);
    return ["make", "model", "year", "body", "series", "variant", "engine", "engine_capacity"]
      .map((field) => [field, params.get(field) || ""])
      .map(([field, value]) => `${field}=${encodeURIComponent(value)}`).join("&");
  }

  restoreHistoryFilters() {
    let state = window.history?.state?.partbotCollection;
    try {
      const key = `partbot_collection_history:${this.store}:${this.collection}`;
      const pending = JSON.parse(window.sessionStorage?.getItem(key) || "null");
      window.sessionStorage?.removeItem(key);
      if (pending?.location === this.currentHistoryLocationKey()) state = pending;
    } catch (error) {
      // History state still works when browser storage is unavailable.
    }
    if (!state || state.store !== this.store || state.collection !== this.collection ||
        state.location !== this.currentHistoryLocationKey()) return;
    const allowed = new Set(this.facets.map(normalizeFacetAttribute));
    if (Array.isArray(state.facets)) state.facets.slice(0, allowed.size).forEach((entry) => {
      if (!Array.isArray(entry) || entry.length !== 2) return;
      const [attribute, values] = entry;
      if (!allowed.has(attribute) || !Array.isArray(values)) return;
      const selected = values.filter((value) => typeof value === "string").slice(0, 100);
      if (selected.length) this.selectedFacetValues.set(attribute, new Set(selected));
    });
    if (["", NATIVE_NEWEST_SORT, "quantity_sold:desc", "price_from:asc", "price_from:desc", "title:asc", "title:desc"].includes(state.sort)) {
      this.currentSortValue = state.sort === "" && !state.sortVersion &&
        resolveSortParam(this.urlSortBy || this.defaultSortBy) === NATIVE_NEWEST_SORT
        ? NATIVE_NEWEST_SORT : state.sort;
      if (this.currentSortValue === NATIVE_NEWEST_SORT && !this.nativeNewestAvailable()) this.currentSortValue = "";
    }
  }

  rememberHistoryFilters() {
    if (this.historyReloading || !window.history?.replaceState || !this.store || !this.collection) return;
    this.historyLocationKey = this.currentHistoryLocationKey();
    this.vehicleHistoryKey = this.currentVehicleHistoryKey();
    const previous = window.history.state && typeof window.history.state === "object" ? window.history.state : {};
    window.history.replaceState({ ...previous, partbotCollection: {
      store: this.store, collection: this.collection, location: this.historyLocationKey,
      facets: Array.from(this.selectedFacetValues || [], ([attribute, values]) => [attribute, Array.from(values)]),
      sort: this.currentSortValue, sortVersion: 2,
    } }, "", window.location.href);
  }

  handleHistoryNavigation() {
    if (this.historyReloading || this.currentHistoryLocationKey() === this.historyLocationKey) return;
    const vehicleChanged = this.currentVehicleHistoryKey() !== this.vehicleHistoryKey;
    const target = window.history?.state?.partbotCollection;
    if (!target || target.store !== this.store || target.collection !== this.collection ||
        target.location !== this.currentHistoryLocationKey()) {
      // The vehicle widget can push intermediate URLs before its async event.
      // Carry the current merchant filters only when that entry has no snapshot.
      this.rememberHistoryFilters();
    }
    try {
      const key = `partbot_collection_history:${this.store}:${this.collection}`;
      window.sessionStorage?.setItem(key, JSON.stringify(window.history?.state?.partbotCollection));
    } catch (error) {
      // Browser storage may be disabled; retain the history-state fallback.
    }
    this.historyReloading = true;
    if (vehicleChanged) this.clearStoredVehicleSelection();
    if (this.hitsContainer) this.hitsContainer.style.display = "none";
    const fallback = this.querySelector("#partbot-product-fallback");
    if (fallback) fallback.hidden = true;
    // The existing vehicle widget initializes from the URL on page load.
    // Reloading keeps URL state authoritative and releases its old React root.
    window.location.reload();
  }

  getVehicleFieldValue(field) {
    if (!field) return "";
    if (typeof field === "object" && field.value !== undefined) return field.value;
    return field;
  }

  getInitialVehicleDetail() {
    const detail = {
      vehicle_ids: [],
      vehicle: {},
    };
    const params = new URLSearchParams(window.location.search);
    const storedVehicle = this.getStoredVehicleSelection() || {};
    const storedFields = storedVehicle.vehicle || {};
    const hasVehicleParam = ["make", "model", "year", "body", "series"].some((field) =>
      params.has(field),
    );

    if (!hasVehicleParam && !storedVehicle.vehicle_ids) return detail;

    ["make", "model", "year", "body", "series"].forEach((field) => {
      const value = hasVehicleParam ? params.get(field) : this.getVehicleFieldValue(storedFields[field]);
      if (!value) return;
      detail.vehicle[field] = {
        value,
        refinement: value,
      };
    });

    if (!hasVehicleParam && Array.isArray(storedVehicle.vehicle_ids)) {
      detail.vehicle_ids = storedVehicle.vehicle_ids.filter(Boolean);
    }

    return detail;
  }

  restoreInitialVehicleSelection() {
    if (!this.showVehicleSearch) return;

    const detail = this.getInitialVehicleDetail();
    const vehicle = detail.vehicle || {};
    const year = this.getVehicleFieldValue(vehicle.year);

    this.selectedYear = year ? parseInt(year, 10) : null;
    this.activeVehicleIds = Array.isArray(detail.vehicle_ids) ? detail.vehicle_ids : [];
    this.vehicle_ids = this.activeVehicleIds.slice();
  }

  extractUnsupportedFacetAttributes(error) {
    const message = error?.message || String(error || "");
    const match = message.match(/Attributes? `([^`]+)` (?:are|is) not filterable/);
    if (!match) return [];

    return match[1]
      .split(",")
      .map((attribute) => attribute.trim())
      .filter(Boolean);
  }

  sanitizeMeiliQueries(queries) {
    if (!Array.isArray(queries) || this.unsupportedFacetAttributes.size === 0) {
      return queries;
    }

    return queries.map((query) => {
      if (!query || !Array.isArray(query.facets)) {
        return query;
      }

      const facets = query.facets.filter(
        (attribute) => !this.unsupportedFacetAttributes.has(attribute),
      );

      return facets.length === query.facets.length ? query : { ...query, facets };
    });
  }

  sanitizeInstantSearchRequests(requests) {
    if (!Array.isArray(requests) || this.unsupportedFacetAttributes.size === 0) {
      return requests;
    }

    return requests.map((request) => {
      if (!request?.params) return request;

      const sanitizedParams = { ...request.params };

      if (Array.isArray(sanitizedParams.facets)) {
        sanitizedParams.facets = sanitizedParams.facets.filter(
          (attribute) => !this.unsupportedFacetAttributes.has(attribute),
        );
      }

      if (Array.isArray(sanitizedParams.disjunctiveFacets)) {
        sanitizedParams.disjunctiveFacets = sanitizedParams.disjunctiveFacets.filter(
          (attribute) => !this.unsupportedFacetAttributes.has(attribute),
        );
      }

      return { ...request, params: sanitizedParams };
    });
  }

  removeUnsupportedFacets(attributes) {
    if (!attributes.length) return [];

    const removedFacets = [];

    attributes.forEach((attribute) => {
      if (this.unsupportedFacetAttributes.has(attribute)) return;
      this.unsupportedFacetAttributes.add(attribute);

      const matchingFacets = this.facets.filter(
        (facet) => normalizeFacetAttribute(facet) === attribute,
      );
      if (matchingFacets.length === 0) return;

      matchingFacets.forEach((facet) => {
        removedFacets.push(facet);
        this.removeFacetContainer(facet);
      });

      this.facets = this.facets.filter((facet) => normalizeFacetAttribute(facet) !== attribute);
    });

    return removedFacets;
  }

  removeFacetContainer(facet) {
    const facetTitle = facet.replace(/_/g, " ").replace("vendor", "brand");
    const containers = Array.from(this.querySelectorAll(".partbot-filter-container"));
    const container = containers.find(
      (node) => node.querySelector(".partbot-filter-title")?.textContent?.trim() === facetTitle,
    );

    if (container) {
      container.remove();
    }
  }

  unsupportedFacetStorageKey() {
    const facetScope = (this.getAttribute("data-facets") || "")
      .toLowerCase()
      .replace(/\s+/g, "_");
    return `partbot_unsupported_facets:v2:${this.searchIndexName || this.store || "default"}:${facetScope}`;
  }

  loadUnsupportedFacetAttributes() {
    try {
      const stored = localStorage.getItem(this.unsupportedFacetStorageKey());
      if (!stored) return;

      JSON.parse(stored).forEach((attribute) => {
        if (attribute) this.unsupportedFacetAttributes.add(attribute);
      });
    } catch (error) {
      console.warn(`${this.debugPrefix} failed to load unsupported facets`, error);
    }
  }

  persistUnsupportedFacetAttributes() {
    try {
      localStorage.setItem(
        this.unsupportedFacetStorageKey(),
        JSON.stringify(Array.from(this.unsupportedFacetAttributes)),
      );
    } catch (error) {
      console.warn(`${this.debugPrefix} failed to persist unsupported facets`, error);
    }
  }

  selectedFacetFilter(excludeAttribute = null) {
    return Array.from(this.selectedFacetValues.entries())
      .filter(([attribute, values]) => attribute !== excludeAttribute && values.size > 0)
      .map(([attribute, values]) => {
        const valueFilters = Array.from(values).map((value) => equalsFilter(attribute, value));
        return `(${valueFilters.join(" OR ")})`;
      })
      .join(" AND ");
  }

  helperRefinementFilter(excludeAttribute = null) {
    const state = this.search?.helper?.state;
    if (!state) return "";

    const groups = [];
    const addGroup = (attribute, values) => {
      if (
        attribute === excludeAttribute ||
        this.selectedFacetValues.has(attribute) ||
        !Array.isArray(values) ||
        values.length === 0
      ) {
        return;
      }

      groups.push(
        `(${values
          .map((value) => equalsFilter(attribute, value, { numeric: attribute === "vehicle_ids" }))
          .join(" OR ")})`,
      );
    };

    Object.entries(state.facetsRefinements || {}).forEach(([attribute, values]) => {
      addGroup(attribute, values);
    });

    Object.entries(state.disjunctiveFacetsRefinements || {}).forEach(([attribute, values]) => {
      addGroup(attribute, values);
    });

    return groups.join(" AND ");
  }

  selectedVehicleYearFilter() {
    if (
      !this.selectedYear ||
      !Array.isArray(this.activeVehicleIds) ||
      this.activeVehicleIds.length === 0
    ) {
      return "";
    }

    const maxIndexedYear = Math.max(this.selectedYear, new Date().getFullYear() + 1);
    const filters = [];

    for (let year = this.selectedYear; year <= maxIndexedYear; year += 1) {
      this.activeVehicleIds.forEach((vehicleId) => {
        filters.push(equalsFilter(`vehicle_years.year_to.${year}`, vehicleId));
      });
    }

    return filters.length > 0 ? `(${filters.join(" OR ")})` : "";
  }

  buildSearchFilters(excludeAttribute = null, { includeHelperRefinements = false } = {}) {
    return [
      this.baseFilters,
      this.collectionProductIdsFilter ? `(${this.collectionProductIdsFilter})` : "",
      this.selectedVehicleYearFilter(),
      this.selectedFacetFilter(excludeAttribute),
      includeHelperRefinements ? this.helperRefinementFilter(excludeAttribute) : "",
    ]
      .map((filter) => String(filter || "").trim())
      .filter(Boolean)
      .join(" AND ");
  }

  baseHitsPerPage() {
    return 12;
  }

  manualProductOrderEnabled() {
    return (
      isManualSortValue(this.defaultSortBy) &&
      this.manualProductIds.length > 0 &&
      !this.currentSortValue
    );
  }

  currentHitsPerPage() {
    if (!this.manualProductOrderEnabled()) return this.baseHitsPerPage();
    return Math.max(this.baseHitsPerPage(), Math.min(this.manualProductIds.length, 50));
  }

  sortManualProductItems(items) {
    if (!this.manualProductOrderEnabled()) return items;

    return [...items].sort((a, b) => {
      const aRank = this.manualProductRank.get(String(a.objectID)) ?? Number.MAX_SAFE_INTEGER;
      const bRank = this.manualProductRank.get(String(b.objectID)) ?? Number.MAX_SAFE_INTEGER;
      return aRank - bRank;
    });
  }

  currentConfigureParams() {
    return {
      ...this.configureWidgetParams,
      hitsPerPage: this.currentHitsPerPage(),
      filters: this.buildSearchFilters(),
    };
  }

  ruleField(rule) {
    const column = String(rule?.column || "").toLowerCase();

    if (column === "vendor") return { attribute: "brand", type: "string", hitFields: ["brand", "vendor"] };
    if (column === "type") return { attribute: "product_type", type: "string", hitFields: ["product_type"] };
    if (column === "tag") return { attribute: "tags", type: "array", hitFields: ["tags"] };
    if (column === "title") return { attribute: "title", type: "string", hitFields: ["title"] };
    if (column === "price") return { attribute: "price_from", type: "number", hitFields: ["price_from"] };
    if (column === "compare_at_price") return { attribute: "compare_at_price_from", type: "number", hitFields: ["compare_at_price_from"] };
    if (column === "inventory_stock") return { attribute: "in_stock", type: "stock", hitFields: ["in_stock"] };
    if (column === "variant_title") return { attribute: "variant_title", type: "string", hitFields: ["variant_title", "variant_titles"] };
    if (column === "category") return { attribute: "category", type: "string", hitFields: ["category", "product_category"] };
    if (column === "weight") return { attribute: "weight", type: "number", hitFields: ["weight", "weight_in_grams"] };

    return null;
  }

  ruleServerFilter(rule) {
    const field = this.ruleField(rule);
    if (!field) return "";

    const relation = rule.relation;
    const value = rule.condition;
    const filterableAttributes = new Set([
      "brand",
      "product_type",
      "tags",
      "price_from",
      "compare_at_price_from",
      "in_stock",
    ]);

    if (!filterableAttributes.has(field.attribute)) return "";

    if (field.type === "number") {
      if (relation === "greater_than") return numericComparisonFilter(field.attribute, ">", value);
      if (relation === "less_than") return numericComparisonFilter(field.attribute, "<", value);
      if (relation === "equals" || relation === "is_equal_to") return numericComparisonFilter(field.attribute, "=", value);
      if (relation === "not_equals" || relation === "is_not_equal_to") return numericComparisonFilter(field.attribute, "!=", value);
      return "";
    }

    if (field.type === "stock") {
      if (relation === "greater_than") return equalsFilter(field.attribute, true);
      if (relation === "less_than") return equalsFilter(field.attribute, false);
      return "";
    }

    if (relation === "equals" || relation === "is_equal_to") {
      return equalsFilter(field.attribute, value);
    }

    if (relation === "not_equals" || relation === "is_not_equal_to") {
      return notEqualsFilter(field.attribute, value);
    }

    return "";
  }

  collectionRulesFilter() {
    if (this.collectionRules.length === 0) return "";

    const serverFilters = this.collectionRules.map((rule) => this.ruleServerFilter(rule));
    const allRulesServerFilterable = serverFilters.every(Boolean);

    if (this.disjunctive === "OR" && !allRulesServerFilterable) {
      return "";
    }

    const usableFilters = serverFilters.filter(Boolean);
    if (usableFilters.length === 0) return "";

    return usableFilters.length === 1
      ? usableFilters[0]
      : `(${usableFilters.join(` ${this.disjunctive} `)})`;
  }

  hasClientSideCollectionRules() {
    if (this.collectionRules.length === 0) return false;

    return this.collectionRules.some((rule) => !this.ruleServerFilter(rule));
  }

  requestsWithCurrentFilters(requests) {
    if (!Array.isArray(requests)) return requests;

    const filters = this.buildSearchFilters();
    return requests.map((request) => {
      if (request?.params) {
        return {
          ...request,
          params: {
            ...request.params,
            filters,
          },
        };
      }

      if (request?.indexUid) {
        return {
          ...request,
          filter: filters,
        };
      }

      return request;
    });
  }

  nativeNewestAvailable() {
    return Boolean(this.collection) && needsCollectionMembership(this.collection);
  }

  searchIndexForCurrentSort() {
    return this.currentSortValue && this.currentSortValue !== NATIVE_NEWEST_SORT
      ? `${this.searchIndexName}:${this.currentSortValue}` : this.searchIndexName;
  }

  async searchCollectionPage(requests, candidateSearch, originalSearch, { materialize = true, nativeOrder = true } = {}) {
    const rank = this.collectionProductRank;
    if (!(rank instanceof Map) || rank.size !== this.totalProducts || rank.size === 0) {
      throw new Error("Incomplete native creation-date order");
    }
    const year = this.selectedYear;
    const vehicleIds = this.activeVehicleIds.slice();
    // Verify the index can return the whole native boundary before trusting a
    // filtered total. Meilisearch caps totals at maxTotalHits; a capped set must
    // fall back instead of silently omitting valid members from Newest.
    const capacity = await candidateSearch([{ indexName: this.searchIndexName, params: {
      query: "", page: 0, hitsPerPage: rank.size, facets: [],
      filters: this.collectionProductIdsFilter, attributesToRetrieve: ["objectID"],
      attributesToHighlight: [], attributesToSnippet: [],
    } }]);
    const indexedIds = capacity.results?.[0]?.hits?.map((hit) => String(hit.objectID)) || [];
    if (indexedIds.length !== rank.size || new Set(indexedIds).size !== rank.size ||
        indexedIds.some((id) => !rank.has(id))) throw new Error("Incomplete Newest index coverage");
    const attributes = new Set(["objectID"]);
    if (year) attributes.add("vehicle_years");
    if (year || this.titleContains.length || this.collectionRules.length) {
      requests.forEach((request) => {
        const facets = request.params?.facets;
        (Array.isArray(facets) ? facets : typeof facets === "string" ? [facets] : [])
          .forEach((facet) => attributes.add(facet));
      });
    }
    if (this.titleContains.length || this.collectionRules.length) attributes.add("title");
    this.collectionRules.forEach((rule) => this.ruleField(rule)?.hitFields.forEach((field) => attributes.add(field)));
    const normalized = nativeOrder
      ? sortedRequestsOnPrimaryIndex(requests, this.searchIndexName, { dropSort: true }) : requests;
    const candidates = normalized.map((request) => {
      if (!request.params || !(request.indexName === this.searchIndexName ||
          request.indexName?.startsWith(`${this.searchIndexName}:`))) {
        throw new Error("Unexpected request for native creation-date order");
      }
      return { ...request, params: { ...request.params, page: 0, hitsPerPage: rank.size,
        attributesToRetrieve: Array.from(attributes), attributesToHighlight: [], attributesToSnippet: [] } };
    });
    const response = await candidateSearch(candidates);
    if (response.results?.length !== requests.length) throw new Error("Incomplete Newest search response");
    const pages = response.results.map((result, index) => {
      const params = normalized[index].params;
      if (!Array.isArray(result.hits) || !Number.isInteger(result.nbHits) ||
          result.nbHits < 0 || result.nbHits > rank.size || result.hits.length !== result.nbHits ||
          result.nbPages !== Math.ceil(result.nbHits / rank.size)) {
        throw new Error("Incomplete Newest candidate set");
      }
      const seen = new Set();
      result.hits.forEach((hit) => {
        const id = String(hit.objectID);
        if (!rank.has(id) || seen.has(id)) throw new Error("Invalid Newest candidate membership");
        seen.add(id);
      });
      const eligible = result.hits.filter((hit) => this.hitMatchesCollectionRules(hit) &&
        this.titleMatchesCollectionRules(hit.title) && this.hitMatchesVehicleYear(hit, year, vehicleIds));
      if (nativeOrder) eligible.sort((a, b) => rank.get(String(a.objectID)) - rank.get(String(b.objectID)));
      const size = Number(params.hitsPerPage ?? this.baseHitsPerPage());
      const page = Number(params.page ?? 0);
      if (!Number.isInteger(size) || size < 0 || !Number.isInteger(page) || page < 0) {
        throw new Error("Invalid Newest pagination");
      }
      let facets = result.facets;
      if (eligible.length !== result.hits.length) {
        facets = Object.fromEntries(Object.keys(result.facets || {}).map((attribute) => {
          const counts = new Map();
          eligible.forEach((hit) => this.facetValuesFromHit(hit, attribute).forEach((value) => {
            const key = String(value);
            counts.set(key, (counts.get(key) || 0) + 1);
          }));
          return [attribute, Object.fromEntries(counts)];
        }));
      }
      return { ids: size ? eligible.slice(page * size, (page + 1) * size).map((hit) => String(hit.objectID)) : [],
        count: eligible.length, size, page, facets };
    });
    const pageRequests = [];
    const pageIndexes = [];
    pages.forEach((page, index) => {
      if (!materialize || !page?.ids.length) return;
      const request = normalized[index];
      const idsFilter = page.ids.map((id) => equalsFilter("objectID", id, { numeric: true })).join(" OR ");
      pageIndexes.push(index);
      pageRequests.push({ ...request, params: { ...request.params, page: 0, hitsPerPage: page.ids.length,
        filters: `(${request.params.filters}) AND (${idsFilter})`, facets: [] } });
    });
    const details = pageRequests.length ? await originalSearch(pageRequests) : { results: [] };
    if (details.results?.length !== pageRequests.length) throw new Error("Incomplete Newest page response");
    const byIndex = new Map(pageIndexes.map((index, position) => [index, details.results[position]]));
    return { ...response, results: response.results.map((result, index) => {
      const page = pages[index];
      if (!page) return result;
      if (!materialize) return { ...result, hits: [], nbHits: page.count, facets: page.facets };
      const hits = byIndex.get(index)?.hits || [];
      const byId = new Map(hits.map((hit) => [String(hit.objectID), hit]));
      if (hits.length !== page.ids.length || byId.size !== page.ids.length ||
          page.ids.some((id) => !byId.has(id))) throw new Error("Newest products changed during pagination");
      return { ...result, hits: page.ids.map((id) => byId.get(id)), page: page.page,
        hitsPerPage: page.size, nbHits: page.count, nbPages: page.size ? Math.ceil(page.count / page.size) : 0, facets: page.facets,
        exhaustiveNbHits: true };
    }) };
  }

  hitMatchesVehicleYear(item, year = this.selectedYear, vehicleIds = this.activeVehicleIds) {
    if (!year || item?.vehicle_years?.year_from === undefined || item?.vehicle_years?.year_to === undefined) return true;
    const from = Object.entries(item.vehicle_years.year_from || {}).filter(([value]) => Number(value) <= year).flatMap(([, ids]) => ids);
    const to = Object.entries(item.vehicle_years.year_to || {}).filter(([value]) => Number(value) >= year).flatMap(([, ids]) => ids);
    return vehicleIds.some((id) => from.includes(id)) && vehicleIds.some((id) => to.includes(id));
  }

  applyCurrentSortToRequests(requests) {
    if (!Array.isArray(requests) || !this.currentSortValue || this.currentSortValue === NATIVE_NEWEST_SORT) return requests;

    return requests.map((request) => ({
      ...request,
      indexName: this.searchIndexName || request.indexName,
      indexUid: this.searchIndexName || request.indexUid,
      params: {
        ...(request.params || {}),
        sort: [this.currentSortValue],
      },
    }));
  }

  updateConfigureWidget() {
    if (!this.search || !this.configureWidget) return;

    this.search.removeWidgets([this.configureWidget]);
    this.configureWidget = instantsearch.widgets.configure(this.currentConfigureParams());
    this.search.addWidgets([this.configureWidget]);
  }

  renderSortControls() {
    const sortByContainer = this.querySelector("#sort-by");
    if (!sortByContainer) return;

    const defaultOptionLabel = isManualSortValue(this.defaultSortBy) ? "Featured" : "Relevance";
    const options = [
      { label: defaultOptionLabel, value: "" },
      ...(this.nativeNewestAvailable() ? [{ label: "Newest", value: NATIVE_NEWEST_SORT }] : []),
      { label: "Best Selling", value: "quantity_sold:desc" },
      { label: "Price (Low to High)", value: "price_from:asc" },
      { label: "Price (High to Low)", value: "price_from:desc" },
      { label: "Alphabetical (A-Z)", value: "title:asc" },
      { label: "Alphabetical (Z-A)", value: "title:desc" },
    ];

    sortByContainer.innerHTML = `
      <div class="partbot-sortby-root">
        <select class="partbot-sortby-select" aria-label="Sort products">
          ${options
            .map(
              (option) => `
                <option value="${option.value}" ${option.value === this.currentSortValue ? "selected" : ""}>
                  ${option.label}
                </option>
              `,
            )
            .join("")}
        </select>
      </div>
    `;

    const select = sortByContainer.querySelector("select");
    select?.addEventListener("change", (event) => {
      this.currentSortValue = event.target.value;
      this.rememberHistoryFilters();
      const nextIndexName = this.searchIndexForCurrentSort();

      this.showSearchLoadingIndicator();
      this.updateConfigureWidget();
      this.search?.helper?.setIndex?.(nextIndexName);
      this.search?.helper?.setPage?.(0);
      this.search?.helper?.search?.();
    });
  }

  hasSelectedFacetValues() {
    return Array.from(this.selectedFacetValues.values()).some((values) => values.size > 0);
  }

  hasActiveFilters() {
    if (this.hasSelectedFacetValues()) return true;
    if (this.selectedYear) return true;
    if (Array.isArray(this.activeVehicleIds) && this.activeVehicleIds.length > 0) return true;
    if (Array.isArray(this.vehicle_ids) && this.vehicle_ids.length > 0) return true;

    const params = new URLSearchParams(window.location.search);
    return this.vehicleParamFields.some((field) => params.has(field));
  }

  setFacetSelection(attribute, value, selected) {
    const selectedValues = this.selectedFacetValues.get(attribute) || new Set();

    if (selected) {
      selectedValues.add(value);
      this.selectedFacetValues.set(attribute, selectedValues);
    } else {
      selectedValues.delete(value);
      if (selectedValues.size > 0) {
        this.selectedFacetValues.set(attribute, selectedValues);
      } else {
        this.selectedFacetValues.delete(attribute);
      }
    }

    this.search?.helper?.setPage?.(0);
    this.rememberHistoryFilters();
    this.renderFacetOptions(attribute);
    this.renderClearFiltersButton();
    this.showSearchLoadingIndicator();
    this.updateConfigureWidget();
    this.scheduleFacetOptionSync();
  }

  clearFacetSelections() {
    if (!this.hasActiveFilters()) return;

    this.selectedFacetValues.clear();
    this.vehicle_ids = [];
    this.activeVehicleIds = [];
    this.selectedYear = null;
    this.clearStoredVehicleSelection();
    this.clearVehicleUrlParams();
    this.rememberHistoryFilters();

    if (this.search?.helper) {
      [
        "vehicle_ids",
        "vehicle_makes",
        "vehicle_years.year_from",
        "vehicle_years.year_to",
      ].forEach((attribute) => this.search.helper.clearRefinements(attribute));
      this.search.helper.setPage(0);
    }

    this.facetOptionState.forEach((_, attribute) => this.renderFacetOptions(attribute));
    this.renderClearFiltersButton();
    this.showSearchLoadingIndicator();
    this.updateConfigureWidget();
    this.scheduleFacetOptionSync();
    document.dispatchEvent(new CustomEvent("partbot-clear-vehicle"));
    window.dispatchEvent(new CustomEvent("partbot-clear-vehicle"));
  }

  clearStoredVehicleSelection() {
    try {
      localStorage.removeItem("partbot_selected_vehicle");
      localStorage.removeItem("partbot_plate_details");
    } catch (error) {
      return;
    }
  }

  clearVehicleUrlParams() {
    const url = new URL(window.location.href);
    let changed = false;

    this.vehicleParamFields.forEach((field) => {
      if (!url.searchParams.has(field)) return;
      changed = true;
      url.searchParams.delete(field);
    });

    if (changed) {
      window.history.replaceState({}, "", url.toString());
    }
  }

  mergeFacetOptions(attribute, facetData, { replace = false } = {}) {
    const options = replace
      ? new Map()
      : new Map(Array.from(this.facetOptionState.get(attribute) || new Map()));

    Object.entries(facetData || {}).forEach(([value, count]) => {
      options.set(value, Number(count) || 0);
    });

    const selectedValues = this.selectedFacetValues.get(attribute);
    if (selectedValues) {
      selectedValues.forEach((value) => {
        if (!options.has(value)) options.set(value, 0);
      });
    }

    this.facetOptionState.set(attribute, options);
  }

  facetOptionsFromResults(results, attribute) {
    if (!results) return {};

    try {
      if (typeof results.getFacetByName === "function") {
        const facet = results.getFacetByName(attribute);
        if (facet?.data && Object.keys(facet.data).length > 0) return facet.data;
      }

      if (Array.isArray(results.disjunctiveFacets)) {
        const disjFacet = results.disjunctiveFacets.find((facet) => facet?.name === attribute);
        if (disjFacet?.data && Object.keys(disjFacet.data).length > 0) return disjFacet.data;
      }

      if (results.facets && results.facets[attribute]) {
        return results.facets[attribute];
      }

      const directFacetDistribution =
        pickFacetDataByAttribute(results.facetDistribution, attribute) ||
        pickFacetDataByAttribute(results.facetsDistribution, attribute);
      if (directFacetDistribution && Object.keys(directFacetDistribution).length > 0) {
        return directFacetDistribution;
      }

      const rawResults = Array.isArray(results._rawResults) ? results._rawResults : [];
      for (const raw of rawResults) {
        const rawFacetDistribution =
          pickFacetDataByAttribute(raw?.facetDistribution, attribute) ||
          pickFacetDataByAttribute(raw?.facetsDistribution, attribute);
        if (rawFacetDistribution && Object.keys(rawFacetDistribution).length > 0) {
          return rawFacetDistribution;
        }
      }
    } catch (error) {
      return {};
    }

    return {};
  }

  renderFacetOptions(attribute) {
    const containerSelector = this.facetContainersByAttribute.get(attribute);
    const container = containerSelector ? this.querySelector(containerSelector) : null;
    if (!container) return;

    const selectedValues = this.selectedFacetValues.get(attribute) || new Set();
    const options = this.facetOptionState.get(attribute) || new Map();
    const items = Array.from(options.entries())
      .filter(([value, count]) => count > 0 || selectedValues.has(value))
      .map(([value, count]) => ({
        value,
        label: value === "true" ? "Yes" : value === "false" ? "No" : value,
        count,
        isRefined: selectedValues.has(value),
      }))
      .sort(
        (a, b) =>
          Number(b.isRefined) - Number(a.isRefined) ||
          a.label.localeCompare(b.label, undefined, { numeric: true }) ||
          b.count - a.count,
      );

    container.innerHTML = `
      <div class="partbot-filter-values partbot-filter-list expanded">
        ${items
          .map(
            ({ value, label, count, isRefined }) => `
              <div class="partbot-filter" data-value="${encodeURIComponent(value)}" style="margin:0 0 14px 0;">
                <label style="display:block;position:relative;min-height:22px;padding-left:28px;line-height:22px;overflow:visible;white-space:nowrap;">
                  <input
                    type="checkbox"
                    ${isRefined ? "checked" : ""}
                    class="accent-red-500 h-4 w-4 rounded border-gray-400 text-red-600 focus:ring-red-600"
                    style="position:absolute;left:0;top:3px;margin:0;width:16px;height:16px;"
                  />
                  <span style="display:block;line-height:22px;">${escapeHtml(label)} (${count})</span>
                </label>
              </div>
            `,
          )
          .join("")}
      </div>
    `;
  }

  renderClearFiltersButton() {
    const container = document.querySelector("#partbot-clear-filters");
    if (!container) return;

    const disabled = !this.hasActiveFilters();
    if (disabled) {
      container.innerHTML = "";
      return;
    }

    const label = container.getAttribute("data-clear-label") || "Clear all";
    container.innerHTML = `<button type="button" class="partbot-clear-filters-button">${escapeHtml(label)}</button>`;

    const button = container.querySelector("button");
    if (button) {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.clearFacetSelections();
      });
    }
  }

  showSearchLoadingIndicator() {
    if (this.searchFailed) return false;
    const status = this.querySelector("#partbot-search-status");
    if (!status) return;

    status.innerHTML = `
      <span style="display:inline-flex;align-items:center;gap:6px;color:#111827;">
        <span>Loading</span>
        <span
          aria-hidden="true"
          style="width:12px;height:12px;border:2px solid rgba(17,24,39,0.25);border-top-color:#111827;border-radius:9999px;display:inline-block;animation:partbot-search-spin 0.7s linear infinite;"
        ></span>
      </span>
    `;

    if (!document.querySelector("#partbot-search-loading-style")) {
      const style = document.createElement("style");
      style.id = "partbot-search-loading-style";
      style.textContent = "@keyframes partbot-search-spin{to{transform:rotate(360deg)}}";
      document.head.appendChild(style);
    }
  }

  renderSearchStatus(count = null) {
    if (this.searchFailed) return false;
    const status = this.querySelector("#partbot-search-status");
    if (!status) return;

    const productCount = Number(count);
    if (!this.hasReceivedSearchResults || !Number.isFinite(productCount)) {
      this.showSearchLoadingIndicator();
      return;
    }

    status.textContent = `Showing ${productCount} ${productCount === 1 ? "product" : "products"}`;
  }

  hideSearchLoadingIndicator() {
    this.renderSearchStatus(this.lastResultsCount);
  }

  ensureResultsContainers() {
    this.hitsContainer = this.querySelector("#partbot-product-grid");
    this.paginationContainer = this.querySelector("#partbot-pagination");

    const fallbackParent =
      this.querySelector(".tw-col-span-1.md\\:tw-col-span-4") ||
      this.querySelector(".tw-col-span-1") ||
      this.querySelector("section") ||
      this;

    if (!this.hitsContainer) {
      this.hitsContainer = document.createElement("div");
      this.hitsContainer.id = "partbot-product-grid";
      fallbackParent.appendChild(this.hitsContainer);
    }

    if (!this.paginationContainer) {
      this.paginationContainer = document.createElement("div");
      this.paginationContainer.id = "partbot-pagination";
      this.paginationContainer.className = "pagination";
      this.hitsContainer.insertAdjacentElement("afterend", this.paginationContainer);
    }
  }

  initializeFacetAccordion() {
    if (!this.facetFiltersContainer || this.facetFiltersContainer.dataset.pbAccordionBound === "true") {
      return;
    }

    this.facetFiltersContainer.dataset.pbAccordionBound = "true";
    this.facetFiltersContainer.addEventListener("click", (event) => {
      const title = event.target.closest(".partbot-filter-title");
      if (!title) return;

      event.preventDefault();
      const values = title.nextElementSibling?.querySelector(".partbot-filter-values");
      if (!values) return;

      const isExpanded = values.classList.contains("expanded");
      values.classList.toggle("expanded", !isExpanded);
      title.classList.toggle("expanded", !isExpanded);
    });
  }

  scheduleFacetOptionSync() {
    if (!this.searchKey || !this.searchHost || !this.searchIndexName) return;

    clearTimeout(this.facetSyncTimer);
    this.facetSyncTimer = setTimeout(() => {
      this.syncFacetOptionCounts().catch((error) => {
        console.warn(`${this.debugPrefix} failed to sync facet option counts`, error);
      });
    }, 200);
  }

  async syncFacetOptionCounts() {
    const attributes = Array.from(this.facetContainersByAttribute.keys());
    if (attributes.length === 0) return;

    const token = ++this.facetSyncToken;

    if (this.nativeNewestAvailable() && (this.currentSortValue === NATIVE_NEWEST_SORT || this.selectedYear)) {
      const sortValue = this.currentSortValue;
      const year = this.selectedYear;
      const query = this.search?.helper?.state?.query || this.titleContainsSearchQuery();
      const requests = attributes.map((attribute) => ({ indexName: this.searchIndexName, params: {
        query, page: 0, hitsPerPage: 1, facets: [attribute],
        filters: this.buildSearchFilters(attribute, { includeHelperRefinements: true }),
      } }));
      const data = await this.searchCollectionPage(requests, this.nativeNewestCandidateSearch,
        this.nativeNewestOriginalSearch, { materialize: false, nativeOrder: false });
      if (token !== this.facetSyncToken || this.currentSortValue !== sortValue || this.selectedYear !== year) return;
      data.results.forEach((result, index) => {
        const attribute = attributes[index];
        this.mergeFacetOptions(attribute, result.facets?.[attribute] || {}, { replace: true });
        this.renderFacetOptions(attribute);
      });
      return;
    }

    if (this.hasClientSideCollectionRules()) {
      await this.syncFacetOptionCountsFromHits(attributes, token);
      return;
    }

    const query = this.search?.helper?.state?.query || "";
    const queries = attributes.map((attribute) => ({
      indexUid: this.searchIndexName,
      q: query,
      limit: 0,
      facets: [attribute],
      filter: this.buildSearchFilters(attribute, { includeHelperRefinements: true }),
    }));

    const response = await fetch(`${this.searchHost}/multi-search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.searchKey}`,
      },
      body: JSON.stringify({ queries }),
    });

    if (!response.ok) {
      throw new Error(`Facet option sync failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (token !== this.facetSyncToken) return;

    (data.results || []).forEach((result, index) => {
      const attribute = attributes[index];
      this.mergeFacetOptions(attribute, result.facetDistribution?.[attribute] || {}, {
        replace: true,
      });
      this.renderFacetOptions(attribute);
    });
  }

  facetValuesFromHit(hit, attribute) {
    const directValue = hit?.[attribute];
    if (directValue !== undefined && directValue !== null) {
      return Array.isArray(directValue) ? directValue : [directValue];
    }

    if (!attribute.includes(".")) return [];

    const value = attribute.split(".").reduce((current, key) => {
      if (current === undefined || current === null) return undefined;
      return current[key];
    }, hit);

    if (value === undefined || value === null) return [];
    return Array.isArray(value) ? value : [value];
  }

  async syncFacetOptionCountsFromHits(attributes, token) {
    const query = this.search?.helper?.state?.query || this.titleContainsSearchQuery();
    const facetCountsByAttribute = new Map(
      attributes.map((attribute) => [attribute, new Map()]),
    );

    const response = await fetch(`${this.searchHost}/indexes/${this.searchIndexName}/search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.searchKey}`,
      },
      body: JSON.stringify({
        q: query,
        limit: 1000,
        filter: this.buildSearchFilters(null, { includeHelperRefinements: true }),
      }),
    });

    if (!response.ok) {
      throw new Error(`Facet hit sync failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (token !== this.facetSyncToken) return;

    (data.hits || [])
      .filter((hit) => this.processHits({ ...hit }))
      .forEach((hit) => {
        attributes.forEach((attribute) => {
          const counts = facetCountsByAttribute.get(attribute);
          this.facetValuesFromHit(hit, attribute).forEach((value) => {
            const normalizedValue = String(value);
            counts.set(normalizedValue, (counts.get(normalizedValue) || 0) + 1);
          });
        });
      });

    attributes.forEach((attribute) => {
      this.mergeFacetOptions(attribute, Object.fromEntries(facetCountsByAttribute.get(attribute)), {
        replace: true,
      });
      this.renderFacetOptions(attribute);
    });
  }

  async initializeSearch() {
    if (!this.apiKey) {
      throw new Error("Missing data-api-key on vehicle-search or partbot-collection-search");
    }

    if (!window.instantsearch) {
      throw new Error("instantsearch.js must be loaded before PartbotCollectionSearch");
    }

    await loadScriptOnce(
      "https://cdn.jsdelivr.net/npm/@meilisearch/instant-meilisearch/dist/instant-meilisearch.umd.min.js",
      "instantMeiliSearch",
    );

    const auth = await fetchSearchConfig(this.apiHost, this.apiKey, this.store);
    this.searchHost = normalizeSearchHost(auth.host, this.searchHost);
    this.searchKey = auth.store_search_key || auth.search_key;
    this.searchIndexName = auth.store_index_name || this.getAttribute("data-algolia-index");


    if (!this.searchKey || !this.searchIndexName) {
      throw new Error("Vehicle auth did not return store_search_key/search_key and store_index_name");
    }

    this.collectionProductIds = await fetchCollectionProductIds(this.collection, this.totalProducts, "created-descending");
    this.collectionProductRank = new Map(this.collectionProductIds.map((id, index) => [id, index]));
    if (needsCollectionMembership(this.collection)) {
      if (this.collectionProductIds.length === 0) throw new Error("Empty collection membership");
      this.collectionProductIdsFilter = this.collectionProductIds
        .map((id) => equalsFilter("objectID", id, { numeric: true }))
        .join(" OR ");
    }

    this.loadUnsupportedFacetAttributes();
    const persistedRemovedFacets = this.removeUnsupportedFacets(
      Array.from(this.unsupportedFacetAttributes),
    );
    if (persistedRemovedFacets.length > 0) {
    }

    const { searchClient } = window.instantMeiliSearch(this.searchHost, this.searchKey);
    const originalSearch = searchClient.search.bind(searchClient);
    const { searchClient: newestClient } = window.instantMeiliSearch(this.searchHost, this.searchKey, { finitePagination: true });
    // Prime the adapter's facet cache with zero hits. Otherwise its initial
    // facet preload would download full product documents for the large ID query.
    let newestReady;
    const candidateSearch = async (requests) => {
      newestReady ||= newestClient.search([{ indexName: this.searchIndexName,
        params: { page: 0, hitsPerPage: 0, facets: [], filters: this.collectionProductIdsFilter } }]);
      await newestReady;
      return newestClient.search(requests);
    };
    this.nativeNewestCandidateSearch = candidateSearch;
    this.nativeNewestOriginalSearch = originalSearch;
    searchClient.search = async (requests) => {
      const sanitizedRequests = this.requestsWithCurrentFilters(
        this.sanitizeInstantSearchRequests(requests),
      );


      const newest = this.currentSortValue === NATIVE_NEWEST_SORT;
      const qualifyYear = Boolean(this.selectedYear) && this.nativeNewestAvailable();
      const execute = (nextRequests) => newest || qualifyYear
        ? this.searchCollectionPage(nextRequests, candidateSearch, originalSearch, { nativeOrder: newest })
        : originalSearch(nextRequests);
      try {
        const response = await execute(sanitizedRequests);
        return response;
      } catch (error) {
        const unsupportedFacetAttributes = this.extractUnsupportedFacetAttributes(error);
        const removedFacets = this.removeUnsupportedFacets(unsupportedFacetAttributes);

        if (removedFacets.length > 0) {
          this.persistUnsupportedFacetAttributes();
          const retriedRequests = this.requestsWithCurrentFilters(this.sanitizeInstantSearchRequests(requests));

          console.warn(`${this.debugPrefix} dropped unsupported facets and retrying`, {
            unsupportedFacetAttributes,
            removedFacets,
          });

          const response = await execute(retriedRequests);
          return response;
        }

        if (isUnsupportedSortError(error)) {
          const retriedRequests = sortedRequestsOnPrimaryIndex(sanitizedRequests, this.searchIndexName, {
            dropSort: true,
          });

          console.warn(`${this.debugPrefix} dropped unsupported sort and retrying`, {
            indexName: this.searchIndexName,
            error,
          });

          const response = await execute(retriedRequests);
          return response;
        }

        console.error(`${this.debugPrefix} search error`, {
          indexName: this.searchIndexName,
          requests: sanitizedRequests,
          error,
        });
        throw error;
      }
    };

    this.search = instantsearch({
      indexName: this.searchIndexForCurrentSort(),
      searchClient,
      routing: false,
      searchFunction: (helper) => {
        this.showSearchLoadingIndicator();
        const titleQuery = this.titleContainsSearchQuery();
        if (titleQuery && helper.state.query !== titleQuery) {
          helper.setQuery(titleQuery);
        }
        if (this.vehicle_ids.length > 0) {
          this.vehicle_ids.map((id) => {
            helper.addDisjunctiveFacetRefinement("vehicle_ids", id);
          });
          this.vehicle_ids = [];
        }
        helper.search();
      },
      onStateChange: ({ uiState, setUiState }) => {
        this.showHitsContainerForVehicleUrl();

        setUiState(uiState);
      },
    });

    let vehicleIdFilter = "";
    if (this.showVehicleSearch && this.platesearch_ids.length > 0) {
      let vehicle_id_filters = this.platesearch_ids
        .map((vehicle_id) => equalsFilter("vehicle_ids", vehicle_id, { numeric: true }))
        .join(" OR ");
      vehicleIdFilter = `${vehicle_id_filters}`;
    }

    let filters = "";
    if (this.brands.length > 0) {
      let brand_filters = this.brands
        .map((brand) => equalsFilter("brand", brand))
        .join(" OR ");
      filters = `(${brand_filters})`;
    }

    if (this.product_types.length > 0) {
      let product_type_filters = this.product_types
        .map((product_type) => {
          // Decode Unicode escape sequences and then escape single quotes
          const decodedType = product_type
            .replace(/\\u[\dA-F]{4}/gi, (match) =>
              String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16)),
            )
          return equalsFilter("product_type", decodedType);
        })
        .join(" OR ");
      filters = filters
        ? `${filters} AND (${product_type_filters})`
        : `(${product_type_filters})`;
    }

    const configuredFilters = normalizeLegacySearchFilters(this.filters);
    if (configuredFilters) {
      filters = filters ? `(${filters}) AND (${configuredFilters})` : `(${configuredFilters})`;
    }

    if (vehicleIdFilter && vehicleIdFilter.length > 0) {
      filters = filters ? `(${filters}) AND (${vehicleIdFilter})` : `(${vehicleIdFilter})`;
    }

    if (this.tags.length > 0) {
      let tag_filters = this.tags
        .map((tag) => equalsFilter("tags", tag))
        .join(" OR ");
      filters = filters
        ? `(${filters}) AND (${tag_filters})`
        : `(${tag_filters})`;
    }

    // add published:true to filters
    filters = filters ? `(${filters}) AND published = true` : `published = true`;
    filters = `(${filters}) AND saleable = true`;

    const configuredFacetAttributes = uniq(
      this.facets.map((facet) => normalizeFacetAttribute(facet)),
    );


    this.baseFilters = filters;
    this.configureWidgetParams = {
      distinct: true,
      maxValuesPerFacet: 100,
      facetingAfterDistinct: true,
      facets: uniq([
        "vehicle_years.year_from",
        "vehicle_years.year_to",
        "vehicle_makes",
      ].concat(configuredFacetAttributes)),
      disjunctiveFacets: uniq([
        "vehicle_years.year_from",
        "vehicle_years.year_to",
        "vehicle_ids",
        "vehicle_makes",
      ].concat(configuredFacetAttributes)),
    };
    this.configureWidget = instantsearch.widgets.configure(this.currentConfigureParams());
    this.search.addWidgets([this.configureWidget]);

    // Add Facet Filters
    if (this.facets && this.facets.length) {
      this.facets.forEach((facet) => {
        let node = document.createElement("div");
        node.classList.add("partbot-filter-container", "group", "expanded");
        let facet_id = facet.replace(/[\s_()]+/g, "-").toLowerCase();
        node.innerHTML = `<div class="partbot-filter-title">${facet
          .replace(/_/g, " ")
          .replace("vendor", "brand")}</div><div id='partbot-filter-${facet_id}'></div>`;

        let attr_title = normalizeFacetAttribute(facet);
        this.facetLabelsByAttribute.set(attr_title, facet);
        this.facetContainersByAttribute.set(attr_title, `#partbot-filter-${facet_id}`);
        this.facetOptionState.set(attr_title, new Map());
        this.facetFiltersContainer.appendChild(node);

        const container = this.querySelector(`#partbot-filter-${facet_id}`);
        if (container) {
          container.addEventListener("change", (event) => {
            if (!event.target.matches('input[type="checkbox"]')) return;

            const itemElement = event.target.closest(".partbot-filter");
            const value = decodeURIComponent(itemElement.getAttribute("data-value") || "");
            if (!value) return;

            this.setFacetSelection(attr_title, value, event.target.checked);
          });
        }

        this.search.addWidgets([
          {
            render: ({ results }) => {
              this.mergeFacetOptions(attr_title, this.facetOptionsFromResults(results, attr_title), {
                replace: true,
              });
              this.renderFacetOptions(attr_title);
            },
          },
        ]);
      });

      this.initializeFacetAccordion();
    }

    // add clear filters button
    this.renderClearFiltersButton();

    this.renderSortControls();
    this.ensureResultsContainers();

    this.search.addWidget(
      instantsearch.widgets.pagination({
        container: this.paginationContainer,
        autoHideContainer: true,
        padding: 1,
        scrollTo: "#partbot-product-grid",
        templates: {
          first: "<<",
          previous: "<",
          next: ">",
          last: ">>",
        },
        cssClasses: {
          list: "partbot-pagination-list",
          selectedItem: "active",
          link: "partbot-pagination-button",
          disabledItem: "disabled hidden",
          firstPageItem: "partbot-pagination-control",
          lastPageItem: "partbot-pagination-control",
          nextPageItem: "partbot-pagination-control",
          previousPageItem: "partbot-pagination-control",
        },
      }),
    );

    // Add Hits Widget
    const filterM = this.filterAlgoliaHits;
    let _this = this;
    this.search.addWidget(
      instantsearch.widgets.hits({
        container: this.hitsContainer,
        cssClasses: {
          list: `partbot-product-grid-list ${this.facets && this.facets.length ? "has-filters" : ""}`,
        },
        templates: {
          item: document.querySelector("#partbot-product-card-template")?.innerHTML || "",
          empty: document.querySelector("#partbot-empty-template")?.innerHTML || "",
        },
        transformItems(items) {
          _this.skus = [];
          return _this.sortManualProductItems(items.filter(filterM, this).filter((i) => i));
        },
      }),
    );

    this.pendingCardLoad = null; // Add this to track pending loads

    this.search.on("error", (error) => {
      console.error(`${this.debugPrefix} search failed`, error);
      this.showSearchFallback();
    });

    this.search.on("render", () => {
      if (this.searchFailed) return;
      this.scheduleFacetOptionSync();
      const results = this.search.helper?.lastResults;
      if (results) {
        const fallback = this.querySelector("#partbot-product-fallback");
        if (fallback) fallback.hidden = true;
        this.hasReceivedSearchResults = true;
        this.lastResultsCount =
          typeof results.nbHits === "number" ? results.nbHits : results.hits?.length;
      }

      this.renderSearchStatus(this.lastResultsCount);
    });

    this.search.start();
    this.showSearchLoadingIndicator();
  }

  cacheVehicleSearchSelection(detail = {}) {
    this.vehicle_ids = Array.isArray(detail.vehicle_ids) ? detail.vehicle_ids : [];
    this.activeVehicleIds = this.vehicle_ids;

    if (detail.vehicle?.year?.value) {
      this.selectedYear = parseInt(detail.vehicle.year.value, 10);
    } else {
      this.selectedYear = null;
    }

    this.showHitsContainerForVehicleUrl();
  }

  showHitsContainerForVehicleUrl() {
    if (this.searchFailed || this.historyReloading) return false;
    if (
      this.hitsContainer &&
      this.activeVehicleIds.length > 0 &&
      window.location.search.includes("make=")
    ) {
      this.hitsContainer.style.display = "block";
    }
  }

  hideGloboInjectedUi() {
    [
      "#gf-tree",
      ".gf-controls-search-form",
      "#gf-controls-container",
      "#gf_pagination_wrap",
      '#gf-products[old-id="globo-product-grid"]',
      "#gf-products",
    ].forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        if (node.id === "partbot-product-grid" || node.id === "partbot-pagination") return;
        node.style.display = "none";
        node.setAttribute("aria-hidden", "true");
      });
    });

    document.querySelectorAll("#gf-grid").forEach((node) => {
      node.style.width = "100%";
      node.style.flex = "1 1 100%";
    });
  }

  handleGloboRenderCompleted() {
    this.hideGloboInjectedUi();
  }

  titleContainsSearchQuery() {
    const values = this.collectionRules
      .filter((rule) => {
        const field = this.ruleField(rule);

        return (
          field?.attribute === "title" &&
          ["contains", "starts_with", "ends_with", "equals", "is_equal_to"]
            .includes(rule.relation)
        );
      })
      .map((rule) => rule.condition);

    return uniq(this.titleContains.concat(values)).join(" ");
  }

  titleMatchesCollectionRules(title) {
    if (this.titleContains.length === 0) return true;

    const normalizedTitle = String(title || "").toLowerCase();
    const matches = this.titleContains.map((value) =>
      normalizedTitle.includes(String(value || "").toLowerCase()),
    );

    return this.disjunctive === "OR"
      ? matches.some(Boolean)
      : matches.every(Boolean);
  }

  hitRuleValues(hit, field) {
    return field.hitFields
      .flatMap((name) => {
        const value = hit?.[name];
        return Array.isArray(value) ? value : [value];
      })
      .filter((value) => value !== undefined && value !== null);
  }

  stringRuleMatches(values, relation, condition) {
    const needle = String(condition || "").toLowerCase();
    const haystacks = values.map((value) => String(value || "").toLowerCase());
    const positiveMatch = haystacks.some((value) => {
      if (relation === "equals" || relation === "is_equal_to") return value === needle;
      if (relation === "starts_with") return value.startsWith(needle);
      if (relation === "ends_with") return value.endsWith(needle);
      if (relation === "contains") return value.includes(needle);
      return false;
    });

    if (relation === "not_equals" || relation === "is_not_equal_to") {
      return haystacks.every((value) => value !== needle);
    }

    if (relation === "does_not_contain" || relation === "not_contains") {
      return haystacks.every((value) => !value.includes(needle));
    }

    return positiveMatch;
  }

  numericRuleMatches(values, relation, condition) {
    const target = Number(condition);
    if (!Number.isFinite(target)) return true;

    return values.some((value) => {
      const actual = Number(value);
      if (!Number.isFinite(actual)) return false;
      if (relation === "greater_than") return actual > target;
      if (relation === "less_than") return actual < target;
      if (relation === "equals" || relation === "is_equal_to") return actual === target;
      if (relation === "not_equals" || relation === "is_not_equal_to") return actual !== target;
      return true;
    });
  }

  stockRuleMatches(values, relation, condition) {
    const stockValue = Number(values[0] === true ? 1 : values[0] || 0);
    const target = Number(condition);
    if (!Number.isFinite(target)) return true;

    if (relation === "greater_than") return stockValue > target;
    if (relation === "less_than") return stockValue < target;
    if (relation === "equals" || relation === "is_equal_to") return stockValue === target;
    if (relation === "not_equals" || relation === "is_not_equal_to") return stockValue !== target;
    return true;
  }

  collectionRuleMatches(rule, hit) {
    const field = this.ruleField(rule);
    if (!field) return true;

    const values = this.hitRuleValues(hit, field);
    if (field.type === "number") return this.numericRuleMatches(values, rule.relation, rule.condition);
    if (field.type === "stock") return this.stockRuleMatches(values, rule.relation, rule.condition);

    return this.stringRuleMatches(values, rule.relation, rule.condition);
  }

  hitMatchesCollectionRules(hit) {
    if (this.collectionRules.length === 0) return true;

    const matches = this.collectionRules.map((rule) => this.collectionRuleMatches(rule, hit));
    return this.disjunctive === "OR"
      ? matches.some(Boolean)
      : matches.every(Boolean);
  }

  processHits(item) {
    if (!this.hitMatchesCollectionRules(item)) {
      return false;
    }

    if (!this.titleMatchesCollectionRules(item.title)) {
      return false;
    }

    if (item.brand && !item.vendor) {
      item.vendor = item.brand;
    }

    if (item.image_url && !item.image) {
      item.image = item.image_url;
    }

    if (item.featured_image && !item.image) {
      item.image = item.featured_image;
    }

    if (!this.hitMatchesVehicleYear(item)) return false;

    // truncate item.title to 140 characters
    if (item.title.length > 140) {
      item.title = item.title.substring(0, 140) + "...";
    }

    // is multi_variant?
    item.multi_variant = item.variant_skus && item.variant_skus.length > 1;

    // check if the item has a compare_at_price_from value and if it is less than or equal to the price_from value set it to null
    if (item.compare_at_price_from && item.compare_at_price_from <= item.price_from) {
      item.compare_at_price_from = null;
    }

    return item;
  }

  applyVehicleSearchSelection(e) {
    if (this.searchFailed || this.historyReloading) return false;
    if (e && this.lastVehicleSearchEvent === e) return false;
    this.lastVehicleSearchEvent = e;

    const detail = e.detail || {};

    // if (this.platesearch_ids && this.platesearch_ids.length) {
    //   return false;
    // }

    this.cacheVehicleSearchSelection(detail);
    this.rememberHistoryFilters();

    if (!this.search?.helper) {
      return false;
    }

    this.showSearchLoadingIndicator();
    this.updateConfigureWidget();

    this.vehicle_ids = [];
    this.search.helper.setPage(0);
    this.search.helper.clearRefinements("vehicle_ids");
    this.search.helper.clearRefinements("vehicle_makes");

    if (detail.vehicle?.make) {
      this.search.helper.addFacetRefinement("vehicle_makes", detail.vehicle.make.value);
    }

    if (this.activeVehicleIds.length > 0) {
      this.activeVehicleIds.map((id) => {
        this.search.helper.addDisjunctiveFacetRefinement("vehicle_ids", id);
      });
    }

    this.search.helper.search();
  }
}

const PRODUCT_CARD_REQUEST_HANDLE_LIMIT = 20;

function fetchProductCardBatch(handles, collection, aspectRatio = "1.0") {
  if (handles.length === 0) {
    return Promise.resolve([]);
  }

  const url = `products/${
    handles[0]
  }?view=partbot-product-cards&c=${collection}&ar=${aspectRatio}${handles
    .slice(1)
    .map((h) => `&h=${encodeURIComponent(h)}`)
    .join("")}&cache=false&type=${new Date().getTime()}`;

  return fetch(window.Shopify.routes.root + url, {
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then((html) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const productCards = doc.querySelectorAll("[data-partbot-product-card]");
      const cardMap = new Map();

      productCards.forEach((card) => {
        const handle = card.dataset.handle;
        cardMap.set(handle, card.outerHTML);
      });

      const returned_handles = handles
        .map((handle) => cardMap.get(handle))
        .filter((card) => card !== undefined);

      return returned_handles;
    });
}

function fetchProductCards(handles, collection, aspectRatio = "1.0") {
  if (handles.length === 0) {
    return Promise.resolve([]);
  }

  const handleBatches = [];
  for (let i = 0; i < handles.length; i += PRODUCT_CARD_REQUEST_HANDLE_LIMIT) {
    handleBatches.push(handles.slice(i, i + PRODUCT_CARD_REQUEST_HANDLE_LIMIT));
  }

  return Promise.all(
    handleBatches.map((batch) => fetchProductCardBatch(batch, collection, aspectRatio)),
  ).then((batchResults) => batchResults.flat());
}

function equalizePartbotCardInfoRows() {
  const cards = Array.from(
    document.querySelectorAll("#partbot-product-grid [data-partbot-product-card]"),
  );

  if (!cards.length) return;

  const rows = new Map();

  cards.forEach((card) => {
    const info = card.querySelector(".card > .card__content:last-child .card__information");
    if (!info) return;

    info.style.height = "";

    const rowTop = Math.round(card.getBoundingClientRect().top);
    const rowKey = Array.from(rows.keys()).find((key) => Math.abs(key - rowTop) <= 2) ?? rowTop;

    if (!rows.has(rowKey)) rows.set(rowKey, []);
    rows.get(rowKey).push(info);
  });

  rows.forEach((items) => {
    const maxHeight = Math.max(...items.map((item) => item.offsetHeight));
    items.forEach((item) => {
      item.style.height = `${maxHeight}px`;
    });
  });
}

function schedulePartbotCardInfoEqualization() {
  requestAnimationFrame(() => {
    equalizePartbotCardInfoRows();
    setTimeout(equalizePartbotCardInfoRows, 150);
  });
}

function loadProductCards(collection, aspectRatio) {
  const existingIndicators = document.querySelectorAll("loading-indicator");
  existingIndicators.forEach((indicator) => indicator.remove());

  const loadingIndicator = document.createElement("loading-indicator");
  const container = document.querySelector("#partbot-product-grid");

  if (!container) return Promise.resolve();

  container.parentElement.appendChild(loadingIndicator);

  const placeholders = Array.from(document.querySelectorAll(".product-card-placeholder"));

  const handles = placeholders.map((placeholder) => placeholder.dataset.handle).filter(Boolean);

  if (handles.length === 0) {
    loadingIndicator.remove();
    return Promise.resolve();
  }

  return fetchProductCards(handles, collection, aspectRatio)
    .then((cardHtmls) => {
      cardHtmls.forEach((cardHtml, index) => {
        const placeholder = placeholders[index];
        if (placeholder && placeholder.parentNode) {
          placeholder.outerHTML = cardHtml;
        }
      });
      schedulePartbotCardInfoEqualization();
    })
    .finally(() => {
      loadingIndicator.remove();
    });
}

let partbotCardEqualizeResizeTimer;
window.addEventListener("resize", () => {
  clearTimeout(partbotCardEqualizeResizeTimer);
  partbotCardEqualizeResizeTimer = setTimeout(schedulePartbotCardInfoEqualization, 150);
});

// move the loading indicator to a component

customElements.define("partbot-collection-search", PartbotCollectionSearch);
